/**
 * The enhanced `.glb`'s **modelled** propeller, turning.
 *
 * `modelled-propeller.ts` decides which of an aeroplane's two propellers is on
 * screen; this turns the one that is. It exists because the previous pass left a
 * gap that the previous pass also made visible: on the four airframes AD-012's
 * effect declines — `AIRCO`, `FE2`, `GOTHA`, `HANDLEY`, the D.H.2, the F.E.2b and
 * the two heavy bombers — the modelled blade is the *only* propeller there is,
 * and it was static geometry with nothing rotating it. A pusher with a frozen
 * propeller at full throttle (user report, 2026-08-24).
 *
 * **One propeller rate in the port, whatever is drawing.** The rate is
 * `propeller-rpm.ts`'s `propellerPhaseStep` — the same integrated phase the disc
 * effect advances, off the same `propellerRpmFraction` read of the same
 * `SimState`. Not a second rate model: what the player sees on a Gotha's nacelle
 * and what he sees on a Camel's nose are the same number, and both agree with
 * what he hears (AD-012 §3's rule, which is why that function is where it is).
 *
 * **And therefore the same answer to aliasing.** A Clerget at full throttle
 * turns 1250 rev/min, and a two-bladed plank spun at 20.8 rev/s in a 60 Hz
 * renderer is a strobe — the pattern has two-fold symmetry, so it repeats every
 * half turn, 41.7 Hz of pattern against a 30 Hz Nyquist limit, and it would
 * appear to crawl backwards at a rate that has nothing to do with the throttle.
 * `propellerPhaseStep` already answers that for the disc by turning the *drawn*
 * propeller at `DISC_TURN_REV_PER_S` — at most 1.6 rev/s — and the same ceiling
 * is what makes an honest, unblurred, real-geometry blade legible here: 3.2 Hz
 * of two-blade pattern, 9.4x under Nyquist, and 9.6 degrees of blade per 60 Hz
 * frame against the 90 degrees at which a two-blade pattern's apparent direction
 * would reverse. Spinning it honestly at the true rate and accepting the strobe
 * was the other option on the table and it was measured and rejected;
 * re-pointing AD-012's disc at a pusher's station was the third and it is a
 * change to the effect's roster the original does not support.
 * `docs/engineering/enhanced-models.md` §12 has the numbers.
 *
 * **Presentation only.** Nothing here reads or writes the hashed sim state: the
 * phase lives in this module's own `WeakMap`, keyed by the scene-graph object the
 * aeroplane is drawn under, and it is advanced by the render loop's wall-clock
 * `dt`. A replay that draws different frames still steps the same sim.
 */

import type * as THREE from "three/webgpu";
import { turnModelledPropellers } from "../three/enhanced-nodes.ts";
import { propellerPhaseStep } from "./propeller-rpm.ts";

/**
 * One accumulated phase per drawn aeroplane, in radians, wrapped to a turn.
 *
 * Keyed by the object the aeroplane hangs under — `/fly`'s player pose group, or
 * a fleet slot's group — both of which outlive the model inside them: ENHANCED
 * MODELS empties and refills them, and the aeroplane keeps its phase across the
 * swap instead of snapping to zero. A `WeakMap` because a slot for a participant
 * who has left the encounter must not be a leak.
 *
 * Only the phase is cached. The propeller *nodes* are re-found every frame by
 * `turnModelledPropellers`, which is a name test over a few tens of nodes and is
 * the only thing that cannot go stale when the geometry under a group is
 * replaced.
 */
const phaseByRoot = new WeakMap<THREE.Object3D, number>();

/**
 * Advances and applies one aeroplane's modelled-propeller phase, and returns the
 * phase it is now at.
 *
 * `rpmFraction` is the *same* per-frame number AD-012's disc is given for this
 * aeroplane — `propellerRpmFraction` for the player, `AircraftView.propellerRpm`
 * for everyone else — so an aeroplane whose engine has stopped and which is not
 * windmilling gets zero, does not advance, and stands perfectly still. That was
 * the last propeller lane's own instrument for reading these captures.
 *
 * Costs nothing on an aeroplane with no modelled propeller: a baked `.TBL` model
 * has no matching node, and neither has an enhanced one whose blade is hidden
 * under the disc — the traverse finds it and sets a quaternion on a node that is
 * not submitted, which is cheaper than testing for it.
 */
export function spinModelledPropeller(root: THREE.Object3D, rpmFraction: number, dtS: number): number {
  const phase = ((phaseByRoot.get(root) ?? 0) + propellerPhaseStep(rpmFraction, dtS)) % (Math.PI * 2);
  phaseByRoot.set(root, phase);
  turnModelledPropellers(root, phase);
  return phase;
}
